import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Clock, Users, BookOpen, Award, Bell, TrendingUp, CheckSquare } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../css/Dashboard.css";
import Loader from "../../components/Loader";
import { Link } from 'react-router-dom';
import DashboardModal from "../../components/Admin/DashboardModal";


function Dashboard() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [tasks, setTasks] = useState([
    { id: 1, title: 'Grade Term Papers', completed: false },
    { id: 2, title: 'Update Student Records', completed: true },
    { id: 3, title: 'Prepare Monthly Report', completed: false },
    { id: 4, title: 'Schedule Parent Meetings', completed: false },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', date: '', time: '' });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', date: '', priority: 'Medium' });
  const [newTask, setNewTask] = useState({ title: '', completed: false });

  // State for attendance pie chart data
  const [studentAttendanceData, setStudentAttendanceData] = useState([
    { name: 'Present', value: 0 },
    { name: 'Absent', value: 0 },
    { name: 'Late', value: 0 },
  ]);

  // State for teacher attendance pie chart data
  const [teacherAttendanceData, setTeacherAttendanceData] = useState([
    { name: 'Present', value: 0 },
    { name: 'Absent', value: 0 },
    { name: 'Late', value: 0 },
  ]);

  // State for active attendance tab (student or teacher)
  const [activeAttendanceTab, setActiveAttendanceTab] = useState('student');

  // State for attendance bar chart data (separate from pie chart)
  const [attendanceBarData, setAttendanceBarData] = useState([]);

  // Fetch all required data on component mount
  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchExams();
    fetchAnnouncements();
    fetchEvents();
    fetchSubjects();
    fetchAttendance();
    fetchTasks();
  }, []);

  // Fetch students data
  const fetchStudents = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch students");
      }

      const data = await response.json();
      setStudents(data);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch attendance data
  const fetchAttendance = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const [studentRes, teacherRes] = await Promise.all([
        fetch("https://schoolpro-api.uniums.com/api/attendance/today/student", {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch("https://schoolpro-api.uniums.com/api/attendance/today/teacher", {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);

      if (!studentRes.ok || !teacherRes.ok) {
        const errorData = !studentRes.ok ? await studentRes.json() : await teacherRes.json();
        throw new Error(errorData.message || "Failed to fetch attendance");
      }

      const [studentRecords, teacherRecords] = await Promise.all([
        studentRes.json(),
        teacherRes.json()
      ]);

      // Process attendance data for pie chart
      const getCount = (records, status) => records.filter(r => r.status === status).length;

      const studentPresent = getCount(studentRecords, 'present');
      const studentAbsent = getCount(studentRecords, 'absent');
      const studentLate = getCount(studentRecords, 'late');

      const teacherPresent = getCount(teacherRecords, 'present');
      const teacherAbsent = getCount(teacherRecords, 'absent');
      const teacherLate = getCount(teacherRecords, 'late');

      // For student pie chart
      setStudentAttendanceData([
        { name: 'Present', value: studentPresent },
        { name: 'Absent', value: studentAbsent },
        { name: 'Late', value: studentLate },
      ]);

      // For teacher pie chart 
      setTeacherAttendanceData([
        { name: 'Present', value: teacherPresent },
        { name: 'Absent', value: teacherAbsent },
        { name: 'Late', value: teacherLate },
      ]);

      // For bar chart - comparing student and teacher attendance
      setAttendanceBarData([
        { name: 'Present', students: studentPresent, teachers: teacherPresent },
        { name: 'Absent', students: studentAbsent, teachers: teacherAbsent },
        { name: 'Late', students: studentLate, teachers: teacherLate },
      ]);

    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error(`Error: ${error.message}`);

      // Set default data if API fails
      setStudentAttendanceData([
        { name: 'Present', value: 42 },
        { name: 'Absent', value: 7 },
        { name: 'Late', value: 5 },
      ]);

      setTeacherAttendanceData([
        { name: 'Present', value: 12 },
        { name: 'Absent', value: 1 },
        { name: 'Late', value: 2 },
      ]);

      setAttendanceBarData([
        { name: 'Present', students: 42, teachers: 12 },
        { name: 'Absent', students: 7, teachers: 1 },
        { name: 'Late', students: 5, teachers: 2 },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch classes data
  const fetchClasses = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/classes", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch classes");
      }

      const data = await response.json();
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch subjects data
  const fetchSubjects = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/subjects", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch subjects");
      }

      const data = await response.json();
      setSubjects(data);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      toast.error(`Error: ${error.message}`);
      // If API fails, set some default subjects
      setSubjects([
        { _id: '1', name: 'Math' },
        { _id: '2', name: 'Science' },
        { _id: '3', name: 'English' },
        { _id: '4', name: 'History' },
        { _id: '5', name: 'Art' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch exams data
  const fetchExams = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/exams", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch exams");
      }

      const data = await response.json();
      setExams(data);
    } catch (error) {
      console.error("Error fetching exams:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  // Function to fetch announcements
  const fetchAnnouncements = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/announcements", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch announcements");
      }

      const data = await response.json();

      // Format announcements for display
      const formattedAnnouncements = data.map(announcement => ({
        id: announcement._id,
        title: announcement.title,
        date: new Date(announcement.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        priority: announcement.priority || 'Medium'
      }));

      setAnnouncements(formattedAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error(`Error: ${error.message}`);
      // Fallback to sample data if API fails
      setAnnouncements([
        { id: 1, title: 'Annual Sports Day', date: 'Apr 15, 2025', priority: 'High' },
        { id: 2, title: 'Parent-Teacher Meeting', date: 'Apr 10, 2025', priority: 'Medium' },
        { id: 3, title: 'Science Fair Registration', date: 'Apr 05, 2025', priority: 'Medium' },
        { id: 4, title: 'Holiday Announcement', date: 'Apr 20, 2025', priority: 'Low' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch upcoming events
  const fetchEvents = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      // Use the /upcoming endpoint to get only future events
      const response = await fetch("https://schoolpro-api.uniums.com/api/events/upcoming", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch events");
      }

      const data = await response.json();

      // Format events for display
      const formattedEvents = data.map(event => ({
        id: event._id,
        name: event.name,
        date: new Date(event.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        time: event.time
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error(`Error: ${error.message}`);
      // Fallback to sample data if API fails
      setEvents([
        { id: 1, name: 'Math Quiz', date: 'Apr 03, 2025', time: '10:00 AM' },
        { id: 2, name: 'Science Project Submission', date: 'Apr 08, 2025', time: '2:00 PM' },
        { id: 3, name: 'School Assembly', date: 'Apr 05, 2025', time: '9:00 AM' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  // Calculate GPA from exams data
  const calculateAverageGPA = () => {
    if (!exams || exams.length === 0) return '0.0';

    let totalGPA = 0;
    exams.forEach(exam => {
      if (exam.averageScore) {
        // Convert score to GPA (this is a simple conversion, adjust as needed)
        const score = exam.averageScore;
        let gpa = 0;

        if (score >= 90) gpa = 4.0;
        else if (score >= 80) gpa = 3.0;
        else if (score >= 70) gpa = 2.0;
        else if (score >= 60) gpa = 1.0;

        totalGPA += gpa;
      }
    });

    return (totalGPA / exams.length).toFixed(1);
  };
  // Function to fetch tasks
  const fetchTasks = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/tasks", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch tasks");
      }

      const data = await response.json();
      setTasks(data.map(task => ({
        id: task._id,
        title: task.title,
        completed: task.completed
      })));
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error(`Error: ${error.message}`);
      // Fallback to sample data if API fails
      setTasks([
        { id: 1, title: 'Grade Term Papers', completed: false },
        { id: 2, title: 'Update Student Records', completed: true },
        { id: 3, title: 'Prepare Monthly Report', completed: false },
        { id: 4, title: 'Schedule Parent Meetings', completed: false },
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  // Colors for charts
  const COLORS = {
    present: '#4caf50',  // Green
    absent: '#f44336',   // Red
    late: '#ff9800',     // Orange
    students: '#3498db', // Blue
    teachers: '#9b59b6'  // Purple
  };

  // Get current date and time
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get current active attendance data based on selected tab
  const currentAttendanceData = activeAttendanceTab === 'student'
    ? studentAttendanceData
    : teacherAttendanceData;

  // Check if attendance data is empty
  const hasAttendanceData = currentAttendanceData.some(item => item.value > 0);



  const handleEventSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const eventData = {
        name: newEvent.name,
        date: newEvent.date,
        time: newEvent.time
      };

      const response = await fetch("https://schoolpro-api.uniums.com/api/events/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add event");
      }

      // Refresh events list
      fetchEvents();

      // Reset form and close modal
      setNewEvent({ name: '', date: '', time: '' });
      setEventModalOpen(false);
      toast.success("Event added successfully!");
    } catch (error) {
      console.error("Error adding event:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const announcementData = {
        title: newAnnouncement.title,
        date: newAnnouncement.date,
        priority: newAnnouncement.priority
      };

      const response = await fetch("https://schoolpro-api.uniums.com/api/announcements/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(announcementData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add announcement");
      }

      // Refresh announcements list
      fetchAnnouncements();

      // Reset form and close modal
      setNewAnnouncement({ title: '', date: '', priority: 'Medium' });
      setAnnouncementModalOpen(false);
      toast.success("Announcement added successfully!");
    } catch (error) {
      console.error("Error adding announcement:", error);
      toast.error(`Error: ${error.message}`);
    }
  };


  const handleTaskSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const taskData = {
        title: newTask.title,
        completed: false
      };

      const response = await fetch("https://schoolpro-api.uniums.com/api/tasks/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(taskData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add task");
      }

      // Refresh tasks list
      fetchTasks();

      // Reset form and close modal
      setNewTask({ title: '', completed: false });
      setTaskModalOpen(false);
      toast.success("Task added successfully!");
    } catch (error) {
      console.error("Error adding task:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Toggle task completion status
  const toggleTaskCompletion = async (taskId, currentStatus) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const response = await fetch(`https://schoolpro-api.uniums.com/api/tasks/${taskId}/toggle`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update task status");
      }

      // Update tasks state locally without fetching again
      setTasks(tasks.map(task =>
        task.id === taskId ? { ...task, completed: !currentStatus } : task
      ));

      toast.success(`Task marked as ${!currentStatus ? 'completed' : 'incomplete'}`);
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error(`Error: ${error.message}`);
    }
  };
  return (
    <div className="dashboard-container">
      {/* Loading indicator */}
      {isLoading && (
        <>
          <Loader />
          <div className="dashboard-loading">
            Loading data...
          </div>
        </>
      )}

      {/* Welcome Section */}
      <div className="dashboard-welcome-section">
        <div className="dashboard-welcome-text">
          <h1>Welcome back, Teacher!</h1>
          <p className="dashboard-date">
            <Calendar size={16} className="dashboard-icon" />
            {currentDate}
          </p>
        </div>
        <div className="dashboard-quick-stats">
          <div className="dashboard-stat-card">
            <Users size={20} className="dashboard-icon" />
            <div>
              <h3>Students</h3>
              <p>{students.length || 0}</p>
            </div>
          </div>
          <div className="dashboard-stat-card">
            <BookOpen size={20} className="dashboard-icon" />
            <div>
              <h3>Classes</h3>
              <p>{classes.length || 0}</p>
            </div>
          </div>
          <div className="dashboard-stat-card">
            <Award size={20} className="dashboard-icon" />
            <div>
              <h3>Average GPA</h3>
              <p>{calculateAverageGPA()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="dashboard-content">
        {/* First Row - Attendance Visualization */}
        <div className="dashboard-row">
          {/* Attendance Pie Chart with Tabs */}
          <div className="dashboard-card dashboard-attendance">
            <div className="dashboard-card-header">
              <h2>Attendance</h2>
              <div className="dashboard-tabs">
                <button
                  className={`dashboard-tab ${activeAttendanceTab === 'student' ? 'dashboard-tab-active' : ''}`}
                  onClick={() => setActiveAttendanceTab('student')}
                >
                  Students
                </button>
                <button
                  className={`dashboard-tab ${activeAttendanceTab === 'teacher' ? 'dashboard-tab-active' : ''}`}
                  onClick={() => setActiveAttendanceTab('teacher')}
                >
                  Teachers
                </button>
              </div>
            </div>
            <div className="dashboard-card-body">
              {hasAttendanceData ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={currentAttendanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {currentAttendanceData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.name === 'Present' ? COLORS.present :
                            entry.name === 'Absent' ? COLORS.absent : COLORS.late}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [
                      `${value} ${activeAttendanceTab === 'student' ? 'students' : 'teachers'}`,
                      null
                    ]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="dashboard-no-data">
                  <p>No attendance data available for {activeAttendanceTab === 'student' ? 'students' : 'teachers'} today</p>
                </div>
              )}
            </div>
          </div>

          {/* Overall Attendance Bar Chart (Students vs Teachers) */}
          <div className="dashboard-card dashboard-attendance-comparison">
            <div className="dashboard-card-header">
              <h2>Overall Attendance Comparison</h2>
            </div>
            <div className="dashboard-card-body">
              {attendanceBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={attendanceBarData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="students" name="Students" fill={COLORS.students} />
                    <Bar dataKey="teachers" name="Teachers" fill={COLORS.teachers} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="dashboard-no-data">
                  <p>No attendance comparison data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Row */}
        <div className="dashboard-row">
          {/* Announcements */}
          <div className="dashboard-card dashboard-announcements">
            <div className="dashboard-card-header">
              <h2><Bell size={16} className="dashboard-icon" /> Announcements</h2>
              <button className="dashboardModal-add-btn" onClick={() => setAnnouncementModalOpen(true)}>+</button>
            </div>
            <div className="dashboard-card-body">
              <ul className="dashboard-announcement-list">
                {announcements.length > 0 ? (
                  announcements.map(announcement => (
                    <li key={announcement.id} className="dashboard-announcement-item">
                      <div className="dashboard-announcement-content">
                        <h3>{announcement.title}</h3>
                        <span className="dashboard-announcement-date">{announcement.date}</span>
                      </div>
                      <span className={`dashboard-priority dashboard-priority-${announcement.priority.toLowerCase()}`}>
                        {announcement.priority}
                      </span>
                    </li>
                  ))
                ) : (
                  <li className="dashboard-no-items">No announcements available</li>
                )}
              </ul>
            </div>
          </div>

          {/* Calendar/Events */}
          <div className="dashboard-card dashboard-events">
            <div className="dashboard-card-header">
              <h2><Calendar size={16} className="dashboard-icon" /> Upcoming Events</h2>
              <button className="dashboardModal-add-btn" onClick={() => setEventModalOpen(true)}>+</button>
            </div>
            <div className="dashboard-card-body">
              <ul className="dashboard-event-list">
                {events.length > 0 ? (
                  events.map(event => (
                    <li key={event.id} className="dashboard-event-item">
                      <div className="dashboard-event-date">
                        <span className="dashboard-event-day">{event.date.split(',')[0]}</span>
                      </div>
                      <div className="dashboard-event-details">
                        <h3>{event.name}</h3>
                        <span className="dashboard-event-time">
                          <Clock size={14} className="dashboard-icon" /> {event.time}
                        </span>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="dashboard-no-items">No upcoming events</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Third Row */}
        <div className="dashboard-row">
          {/* Tasks/To-Do */}
          <div className="dashboard-card dashboard-tasks">
            <div className="dashboard-card-header">
              <h2><CheckSquare size={16} className="dashboard-icon" /> Tasks</h2>
            </div>
            <div className="dashboard-card-body">
              <ul className="dashboard-task-list">
                {tasks.map(task => (
                  <li key={task.id} className="dashboard-task-item">
                    <div
                      className={`dashboard-task-checkbox ${task.completed ? 'dashboard-task-completed' : ''}`}
                      onClick={() => toggleTaskCompletion(task.id, task.completed)}
                    >
                      {task.completed && <span>✓</span>}
                    </div>
                    <span className={`dashboard-task-title ${task.completed ? 'dashboard-task-title-completed' : ''}`}>
                      {task.title}
                    </span>
                  </li>
                ))}
              </ul>
              <button className="dashboard-add-task-btn" onClick={() => setTaskModalOpen(true)}>Add New Task</button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="dashboard-card dashboard-quick-links">
            <div className="dashboard-card-header">
              <h2>Quick Access</h2>
            </div>
            <div className="dashboard-card-body">
              <div className="dashboard-quick-links-grid">
                <Link to="/attendance/take">
                  <div className="dashboard-quick-link-item">
                    <div className="dashboard-quick-link-icon dashboard-icon-attendance">
                      <Users size={24} />
                    </div>
                    <span>Attendance</span>
                  </div>
                </Link>
                <Link to="/exam/grades">
                  <div className="dashboard-quick-link-item">
                    <div className="dashboard-quick-link-icon dashboard-icon-grades">
                      <Award size={24} />
                    </div>
                    <span>Grades</span>
                  </div>
                </Link>
                <Link to="/routine">
                  <div className="dashboard-quick-link-item">
                    <div className="dashboard-quick-link-icon dashboard-icon-schedule">
                      <Clock size={24} />
                    </div>
                    <span>Schedule</span>
                  </div>
                </Link>
                <Link to="/reports">
                  <div className="dashboard-quick-link-item">
                    <div className="dashboard-quick-link-icon dashboard-icon-reports">
                      <TrendingUp size={24} />
                    </div>
                    <span>Reports</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DashboardModal
        isOpen={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        title="Add New Event"
      >
        <form onSubmit={handleEventSubmit} className="dashboardModal-form">
          <div className="dashboardModal-form-group">
            <label htmlFor="eventName">Event Name</label>
            <input
              type="text"
              id="eventName"
              value={newEvent.name}
              onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-group">
            <label htmlFor="eventDate">Date</label>
            <input
              type="date"
              id="eventDate"
              value={newEvent.date}
              onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-group">
            <label htmlFor="eventTime">Time</label>
            <input
              type="time"
              id="eventTime"
              value={newEvent.time}
              onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-actions">
            <button type="button" onClick={() => setEventModalOpen(false)}>Cancel</button>
            <button type="submit">Save Event</button>
          </div>
        </form>
      </DashboardModal>

      {/* Announcement Modal */}
      <DashboardModal
        isOpen={announcementModalOpen}
        onClose={() => setAnnouncementModalOpen(false)}
        title="Add New Announcement"
      >
        <form onSubmit={handleAnnouncementSubmit} className="dashboardModal-form">
          <div className="dashboardModal-form-group">
            <label htmlFor="announcementTitle">Announcement Title</label>
            <input
              type="text"
              id="announcementTitle"
              value={newAnnouncement.title}
              onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-group">
            <label htmlFor="announcementDate">Date</label>
            <input
              type="date"
              id="announcementDate"
              value={newAnnouncement.date}
              onChange={(e) => setNewAnnouncement({ ...newAnnouncement, date: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-group">
            <label htmlFor="announcementPriority">Priority</label>
            <select
              id="announcementPriority"
              value={newAnnouncement.priority}
              onChange={(e) => setNewAnnouncement({ ...newAnnouncement, priority: e.target.value })}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div className="dashboardModal-form-actions">
            <button type="button" onClick={() => setAnnouncementModalOpen(false)}>Cancel</button>
            <button type="submit">Save Announcement</button>
          </div>
        </form>
      </DashboardModal>

      {/* Task Modal */}
      <DashboardModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        title="Add New Task"
      >
        <form onSubmit={handleTaskSubmit} className="dashboardModal-form">
          <div className="dashboardModal-form-group">
            <label htmlFor="taskTitle">Task Title</label>
            <input
              type="text"
              id="taskTitle"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              required
            />
          </div>
          <div className="dashboardModal-form-actions">
            <button type="button" onClick={() => setTaskModalOpen(false)}>Cancel</button>
            <button type="submit">Save Task</button>
          </div>
        </form>
      </DashboardModal>
      <ToastContainer />
    </div>
  );
}

export default Dashboard;